import { computed, type ComputedRef } from 'vue';
import type { Options } from 'highcharts';

type WorkloadChartSeriesData = {
    type: 'bar';
    name: string;
    data: number[];
}[];

type WorkloadCategoriesAndSeriesData = {
    xAxisCategories: string[];
    chartSeries: WorkloadChartSeriesData;
};

type AssignmentsChartSeriesData = ({
    type: 'column';
    name: string;
    yAxis: string;
    color: string;
    stack?: string;
    data: number[];
} | {
    type: 'line';
    name: string;
    yAxis: string;
    color: string;
    data: number[];
})[];

type AssignmentsCategoriesAndSeriesData = {
    xAxisCategories: string[];
    chartSeries: AssignmentsChartSeriesData;
};

type AssignmentOpenByWorkloadSeriesData = ({
    type: 'column';
    name: string;
    data: number[][];
})[];

type AssignmentOpenByWorkloadCategoriesAndSeriesData = {
    xAxisCategories: string[];
    chartSeries: AssignmentOpenByWorkloadSeriesData;
}

type AssignmentCompletionTimeByWorkloadSeriesData = ({
    type: 'bar';
    name: string;
    data: number[][];
})[];

type AssignmentCompletionTimeByWorkloadCategoriesAndSeriesData = {
    xAxisCategories: string[];
    chartSeries: AssignmentCompletionTimeByWorkloadSeriesData;
}

export function useWorkloadChartConfigs(
    workloadSeries: ComputedRef<WorkloadCategoriesAndSeriesData>,
    assignmentsSeries?: ComputedRef<AssignmentsCategoriesAndSeriesData>,
    openAssignmentsByWorkloadChartSeries?: ComputedRef<AssignmentOpenByWorkloadCategoriesAndSeriesData>,
    assignmentsCompletionTimeByWorkloadChartSeries?: ComputedRef<AssignmentCompletionTimeByWorkloadCategoriesAndSeriesData>
) {
    const workloadAgeChartOptions = computed(() => {

        return ({
            chart: { type: 'bar' },
            title: { text: '' },
            tooltip: {
                enabled: true,
                shared: true
            },
            legend: {
                symbolRadius: 0,
                reversed: true
            },
            xAxis: {
                title: { text: 'Total Assignments' },
                categories: workloadSeries.value.xAxisCategories,
                allowDecimals: false
            },
            yAxis: { title: { text: '' } },
            plotOptions: {
                series: {
                    stacking: 'normal',
                    dataLabels: { enabled: false }
                }
            },
            series: [...workloadSeries.value.chartSeries].reverse(),
            credits: { enabled: false }
        } as Partial<Options>);
    });

    const assignmentsInOutChartOptions = computed(() => {
        if (!assignmentsSeries) {
            return {} as Partial<Options>;
        }

        return ({
            title: { text: '' },
            tooltip: {
                enabled: true,
                shared: true
            },
            legend: { symbolRadius: 0 },
            xAxis: { categories: assignmentsSeries.value.xAxisCategories },
            yAxis: [
                {
                    id: 'yaxis-left',
                    allowDecimals: false,
                    title: { text: 'Number of Assignments' }
                },
                {
                    id: 'yaxis-right',
                    allowDecimals: false,
                    opposite: true,
                    title: { text: 'Backlog' }
                }
            ],
            plotOptions: {
                column: {
                    stacking: 'normal',
                    dataLabels: { enabled: false }
                }
            },
            series: assignmentsSeries.value.chartSeries,
            credits: { enabled: false }
        } as Partial<Options>);
    });

    const openAssignmentsByWorkloadChartOptions = computed(() => {
        if (!openAssignmentsByWorkloadChartSeries) {
            return {} as Partial<Options>;
        }
        return ({

            title: { text: '' },
            tooltip: {
                enabled: true,
                shared: true
            },
            xAxis: { categories: openAssignmentsByWorkloadChartSeries.value.xAxisCategories },
            yAxis: { title: { text: 'Number of Open Assignments' } },
            plotOptions: {
                series: {
                    stacking: 'normal',
                    dataLabels: { enabled: true }
                }
            },
            series: openAssignmentsByWorkloadChartSeries.value.chartSeries,
            credits: { enabled: false }
        } as Partial<Options>);
    });

    const assignmentsCompletionTimeByWorkloadChartOptions = computed(() => {
        if (!assignmentsCompletionTimeByWorkloadChartSeries) {
            return {} as Partial<Options>;
        }

        return ({
            chart: { type: 'bar' },
            title: { text: '' },
            tooltip: {
                enabled: true,
                shared: true
            },
            xAxis: { categories: assignmentsCompletionTimeByWorkloadChartSeries.value.xAxisCategories },
            yAxis: { title: { text: '(in minutes)' } },
            plotOptions: {
                series: {
                    stacking: 'normal',
                    dataLabels: { enabled: true }
                }
            },
            series: assignmentsCompletionTimeByWorkloadChartSeries.value.chartSeries,
            credits: { enabled: false }
        } as Partial<Options>);
    });

    return {
        workloadAgeChartOptions,
        assignmentsInOutChartOptions,
        openAssignmentsByWorkloadChartOptions,
        assignmentsCompletionTimeByWorkloadChartOptions
    };
}